import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import WorkIcon from '@mui/icons-material/Work';
import SEOHead from '../components/SEOHead';

function TrabajaConNosotros() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleProviderClick = () => {
    window.location.href = 'mailto:hola@voybus.com?subject=Quiero ser proveedor/partner';
  };

  const handleTeamClick = () => {
    window.location.href = 'mailto:hola@voybus.com?subject=Quiero trabajar en el equipo';
  };

  return (
    <>
      <SEOHead
        title="Trabaja Con Nosotros - VoyBus"
        description="Únete al equipo de VoyBus. Buscamos personas apasionadas por los viajes para formar parte de nuestra agencia de viajes uruguaya."
        keywords={['trabajo', 'empleo', 'VoyBus', 'agencia de viajes', 'carrera']}
        url="/trabaja-con-nosotros"
        type="website"
      />
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .print-content, .print-content * {
              visibility: visible;
            }
            .print-content {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              background: white;
            }
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>
      <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa', py: 4 }} className="print-content">
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.4rem', sm: '1.75rem', md: '2.5rem' },
              mb: 2,
              color: 'text.primary',
            }}
          >
            🚀 Trabajá con Nosotros
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            Nos mueve crear experiencias de viaje que la gente recuerde con una sonrisa.
            Si compartís esa misma pasión, nos encantaría conocerte.
          </Typography>

          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, fontWeight: 500 }}>
            Esta sección es para vos si:
          </Typography>
          <Box sx={{ pl: { xs: 2, md: 3 }, mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
              ✨ Tenés un alojamiento o servicio turístico y querés ser parte de nuestros viajes
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              ✨ Querés sumarte como coordinador/a o colaborar con el equipo
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, fontStyle: 'italic', textAlign: 'center' }}>
            Elegí el camino que más te identifique 👇
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ 
                fontWeight: 600, 
                mb: 2, 
                color: 'primary.main',
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
              }}
            >
              🏨 ¿Sos un Alojamiento u Operador Turístico?
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              Nos gusta trabajar con aliados que compartan nuestra forma de hacer las cosas: con compromiso, cercanía y foco en el viajero.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              Si tenés un servicio que pueda complementar nuestros viajes, estamos abiertos a escucharte.
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontWeight: 500 }}>
              Buscamos sumar a nuestra red:
            </Typography>
            <Box sx={{ pl: { xs: 2, md: 3 }, mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                ✅ Hoteles, hostales, posadas y alojamientos
              </Typography>
              <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                🚌 Empresas de transporte y operadores turísticos
              </Typography>
              <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                🎟️ Experiencias, excursiones y actividades locales
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                🥘 Propuestas gastronómicas, bodegas, balnearios, atracciones y eventos
              </Typography>
            </Box>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontWeight: 500 }}>
              ¿Por qué trabajar juntos?
            </Typography>
            <Box sx={{ pl: { xs: 2, md: 3 }, mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                🤝 Relaciones simples, humanas y de confianza
              </Typography>
              <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                📍 Posibilidad de incluir tu servicio en nuestros circuitos y paquetes
              </Typography>
              <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                🌱 Crecimiento comercial mutuo
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                ✨ Proyección dentro de una red que está expandiéndose
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }} className="no-print">
              <Button
                variant="contained"
                color="primary"
                startIcon={<BusinessIcon />}
                onClick={handleProviderClick}
                size="large"
                sx={{
                  px: 6,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 4,
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Quiero ser proveedor/partner
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ 
                fontWeight: 600, 
                mb: 2, 
                color: 'primary.main',
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
              }}
            >
              🧑‍💼 ¿Querés unirte al equipo?
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              Estamos formando un equipo con buena energía, iniciativa y pasión por viajar.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              Si te gusta acompañar personas, resolver, organizar y disfrutar del camino… capaz tenés un lugar acá.
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontWeight: 500 }}>
              Algunos perfiles que solemos buscar:
            </Typography>
            <Box sx={{ pl: { xs: 2, md: 3 }, mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8, fontWeight: 500 }}>
                🧭 Coordinadores/as de viaje
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.8, pl: 2, fontStyle: 'italic' }}>
                Para acompañar grupos y asegurarse de que todo fluya durante la experiencia.
              </Typography>
              <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8, fontWeight: 500 }}>
                📲 Colaboradores/as para ventas, atención y logística
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.8, pl: 2, fontStyle: 'italic' }}>
                Personas proactivas, con buena comunicación y ganas de aportar.
              </Typography>
              <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8, fontWeight: 500 }}>
                📣 Promotores/as o embajadores/as de marca
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.8, pl: 2, fontStyle: 'italic' }}>
                Si te encanta recomendar viajes o tenés llegada a personas que disfrutan viajar, podemos crecer juntos.
              </Typography>
            </Box>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontWeight: 500 }}>
              Valoramos especialmente:
            </Typography>
            <Box sx={{ pl: { xs: 2, md: 3 }, mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                ✨ Buena energía y empatía
              </Typography>
              <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                🌍 Pasión por crear experiencias memorables
              </Typography>
              <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                🧠 Espíritu colaborativo y ganas de aprender
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                💬 Comunicación clara y respeto
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }} className="no-print">
              <Button
                variant="contained"
                color="primary"
                startIcon={<WorkIcon />}
                onClick={handleTeamClick}
                size="large"
                sx={{
                  px: 6,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 4,
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Quiero trabajar en el equipo
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ 
                fontWeight: 600, 
                mb: 2, 
                color: 'primary.main',
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
              }}
            >
              💛 Nuestra Forma de Trabajar
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              Si te sumás, vas a encontrar:
            </Typography>
            <Box sx={{ pl: { xs: 2, md: 3 }, mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                • Un ambiente humano, cercano y sin burocracias innecesarias
              </Typography>
              <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                • Autonomía, confianza y libertad para proponer ideas
              </Typography>
              <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                • Un lugar donde se escucha y se valora al equipo
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                • Transparencia, respeto y propósito
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontStyle: 'italic' }}>
              No buscamos solo currículums — buscamos personas que encajen con la cultura y disfruten del viaje tanto como quienes se suben al bus.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ 
                fontWeight: 600, 
                mb: 2, 
                color: 'primary.main',
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
              }}
            >
              ✉️ ¿Charlamos?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
              No importa si estás empezando, si ya tenés experiencia o si tu servicio es nuevo.
              Si sentís que hay buena vibra y podemos sumar juntos, te invitamos a escribirnos.
            </Typography>

            <Box sx={{ mt: 4, p: 3, backgroundColor: '#fff', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.8, textAlign: 'center' }}>
                Contactanos por{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  email: hola@voybus.com
                </Box>
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}

export default TrabajaConNosotros;

